import {MRT_ColumnDef} from 'material-react-table';

import {EventLink} from '@entities/event';
import {CurrencyValue, rightAlignmentColumnProps} from '@shared/components';
import {DATETIME_FORMAT, ROUTE__TRANSACTION_FULL_INFO} from '@shared/constants';
import {formatInTz} from '@shared/utils';
import {Transaction} from '../types';
import {PaymentStatusLabel} from './PaymentStatusLabel';
import {RelatedEntityLink} from './RelatedEntityLink';
import {ShortIdLink} from './ShortIdLink';
import {TransactionTypeLabel} from './TransactionTypeLabel';

const createdColumn: MRT_ColumnDef<Transaction> = {
  accessorKey: 'created',
  header: 'Created',
  Cell: ({row}) => formatInTz(row.original.created, DATETIME_FORMAT),
  size: 160,
};

const realColumn: MRT_ColumnDef<Transaction> = {
  accessorKey: 'value',
  header: 'Real',
  Cell: ({row}) => <CurrencyValue value={row.original.value} />,
  size: 120,
  ...rightAlignmentColumnProps,
};

const promoColumn: MRT_ColumnDef<Transaction> = {
  accessorKey: 'bonus',
  header: 'Promo',
  Cell: ({row}) => <CurrencyValue value={row.original.bonus} />,
  size: 120,
  ...rightAlignmentColumnProps,
};

const formatLocalAmount = (value: number | null, currency: string) => {
  if (value === null || !currency) {
    return '—';
  }

  return <CurrencyValue value={value} prefix="" suffix={` ${currency}`} />;
};

const realLocalColumn: MRT_ColumnDef<Transaction> = {
  accessorKey: 'localValue',
  header: 'Real (local)',
  Cell: ({row}) =>
    formatLocalAmount(row.original.localValue, row.original.currency),
  size: 140,
  ...rightAlignmentColumnProps,
};

const promoLocalColumn: MRT_ColumnDef<Transaction> = {
  accessorKey: 'localBonus',
  header: 'Promo (local)',
  Cell: ({row}) =>
    formatLocalAmount(row.original.localBonus, row.original.currency),
  size: 140,
  ...rightAlignmentColumnProps,
};

const paymentLocalColumn: MRT_ColumnDef<Transaction> = {
  ...realLocalColumn,
  header: 'Amount (local)',
};

export const transactionColumns: MRT_ColumnDef<Transaction>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    Cell: ({row}) => (
      <ShortIdLink
        id={row.original.id}
        to={ROUTE__TRANSACTION_FULL_INFO.replace(':id', row.original.id)}
      />
    ),
    size: 180,
  },
  createdColumn,
  {
    accessorKey: 'txnType',
    header: 'Type',
    Cell: ({row}) => (
      <TransactionTypeLabel
        txnType={row.original.txnType}
        paymentType={row.original.paymentType}
      />
    ),
    size: 180,
  },
  realColumn,
  promoColumn,
  realLocalColumn,
  promoLocalColumn,
  {
    accessorKey: 'coins',
    header: 'Coins',
    size: 100,
    ...rightAlignmentColumnProps,
  },
  {
    accessorKey: 'relatedEntity',
    header: 'Related',
    Cell: ({row}) => <RelatedEntityLink transaction={row.original} />,
    size: 140,
  },
  {
    accessorKey: 'eventId',
    header: 'Event ID',
    Cell: ({row}) => {
      const {eventId} = row.original;

      if (!eventId) {
        return null;
      }

      return <EventLink eventId={eventId} />;
    },
    size: 140,
  },
];

/**
 * Deposits and withdrawals share these columns but open different pages,
 * so the details route is passed in
 */
export const createPaymentColumns = (
  detailsRoute: string,
  {showLocalAmount = false}: {showLocalAmount?: boolean} = {},
): MRT_ColumnDef<Transaction>[] => [
  {
    accessorKey: 'id',
    header: 'ID',
    Cell: ({row}) => (
      <ShortIdLink
        id={row.original.id}
        to={detailsRoute
          .replace(':userId', row.original.userId)
          .replace(':id', row.original.id)}
      />
    ),
    size: 180,
  },
  {
    accessorKey: 'label',
    header: 'Status',
    Cell: ({row}) => <PaymentStatusLabel status={row.original.label} />,
    size: 140,
  },
  createdColumn,
  {
    ...realColumn,
    header: 'Amount',
  },
  ...(showLocalAmount ? [paymentLocalColumn] : []),
  {
    id: 'paymentMethod',
    header: 'Method',
    accessorFn: ({method}) =>
      method ? `${method.name} (${method.provider})` : '—',
    size: 180,
  },
];
